import { supabase } from "@/lib/supabase";

interface SendPushParams {
  title: string;
  body: string;
  targetUserId?: string; // if undefined, send to all
}

/**
 * Send push notification — proxies via the `send-push-notification` Edge
 * Function because the Expo Push API doesn't allow CORS from browsers.
 * The Edge Function handles auth, token lookup, Expo API call, batching,
 * and history logging. See supabase/functions/send-push-notification.
 */
export async function sendPushNotification({
  title,
  body,
  targetUserId,
}: SendPushParams): Promise<{ sent: number; failed: number }> {
  if (!supabase) throw new Error("Supabase not configured");

  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error("No hay sesión activa");

  const url = `${import.meta.env.VITE_SUPABASE_URL || ""}/functions/v1/send-push-notification`;

  const res = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      "Content-Type": "application/json",
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY || "",
    },
    body: JSON.stringify({
      title,
      body,
      target_user_id: targetUserId,
    }),
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Error al enviar push (${res.status}): ${errorText}`);
  }

  const result = await res.json();
  return { sent: result.sent ?? 0, failed: result.failed ?? 0 };
}

export async function getNotificationHistory() {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from("push_notifications")
    .select("*")
    .order("created_at", { ascending: false })
    .limit(50);
  if (error) throw error;
  return data || [];
}
